import { AsyncSelectInputOption } from '@/components/Forms/AsyncSelect';
import { AddProjectTransactionForm } from '@/components/Payouts/admin/AddProjectTransactionForm';
import { projectTransactionSchema } from '@/components/Payouts/admin/AddProjectTransactionModal';
import {
  emptyPayeeTransactionValues,
  hasSelectedPayee,
  PayeeOrganizationSelect,
  PayeeTransactionFields,
  PayeeTransactionFormValues,
  payeeTransactionSchema,
  toCreatePayeeTransactionPayload,
} from '@/components/Payouts/admin/payeeTransactionForm';
import { useCreatePayeeTransactionMutation } from '@/services/payouts';
import { Alert, Box, Button, FormikTextInput, Modal, toast } from '@palmetto/palmetto-components';
import { Field, Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import * as yup from 'yup';

import { CreateProjectTransactionPayload, PayeeType } from 'types';

/**
 * Both halves of the form live under one Formik, which works because each renders through Formik
 * context rather than props. Only one half is ever visible, so the fields they share — event, amount,
 * description — are the same value, and switching branches has to clear it.
 *
 * Spelled out rather than intersecting CreateProjectTransactionPayload: that type is a Partial of a
 * mapped type, and intersecting it here made the union too complex for the compiler to represent.
 */
interface AddTransactionFormValues extends PayeeTransactionFormValues {
  projectExternalId: string;
  payeeId?: string;
  payeeType?: PayeeType;
  clawbackRuleName?: string;
  prePayment: boolean;
  historicalAdjustment: boolean;
  /** Held as the display-formatted string the project form's date input writes. */
  eventDate?: string;
}

const ACCOUNT_ID_LENGTH = 24;

const emptyProjectValues = {
  amount: '' as unknown as number,
  prePayment: false,
  event: '' as unknown as undefined,
  description: '',
  clawbackRuleName: undefined,
};

/**
 * The fields that belong to only one branch, cleared when the other one is entered.
 *
 * `eventDate`, not `transactionDate`: the project form's date input carries `name="transactionDate"`
 * but reads and writes `values.eventDate`, so the name attribute is the only place that string
 * appears. Resetting it cleared nothing while the real date survived the switch.
 */
const projectOnlyFields = {
  historicalAdjustment: false,
  eventDate: undefined,
  payeeId: undefined,
  clawbackRuleName: undefined,
};
const payeeOnlyFields = { generalLedgerCategory: '' };
const sharedFields = { event: '', amount: '', description: '' };

interface AddTransactionFormFieldsProps {
  showOrganizationForm: boolean;
  hideAccountSelection: boolean;
  payeeType?: PayeeType;
  allowHistoricalAdjustment: boolean;
  showFincoEPCOption: boolean;
}

const AddTransactionFormFields = ({
  showOrganizationForm,
  hideAccountSelection,
  payeeType,
  allowHistoricalAdjustment,
  showFincoEPCOption,
}: AddTransactionFormFieldsProps) => {
  const { values, setValues, isSubmitting } = useFormikContext<AddTransactionFormValues>();

  const hasAccountId = Boolean(values.projectExternalId);
  const hasPayee = hasSelectedPayee(values);

  /**
   * Entering one branch clears the other's values along with the shared ones. Without this, filling
   * the project form, clearing the account id and then picking an organization would carry a
   * project payout event into a payee transaction, where it is not a valid event at all.
   */
  const handlePayeeSelected = useCallback(
    (option: AsyncSelectInputOption | '') => {
      if (!option) return;
      // Writes the payee here too. The select sets it with setFieldValue in the same tick, so an
      // updater that only spread `current` would read the pre-selection values and undo it.
      void setValues(
        (current) =>
          ({ ...current, payee: option, ...sharedFields, ...projectOnlyFields, projectExternalId: '' }) as any,
      );
    },
    [setValues],
  );

  const handleAccountIdChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const projectExternalId = event.target.value;
      const isEnteringBranch = !hasAccountId && Boolean(projectExternalId);

      void setValues(
        (current) =>
          ({
            ...current,
            projectExternalId,
            ...(isEnteringBranch ? { ...sharedFields, ...payeeOnlyFields, payee: '' } : {}),
          }) as any,
      );
    },
    [hasAccountId, setValues],
  );

  return (
    <Box childGap={{ base: 'lg', desktop: 'xl' }}>
      {/* On an account page the account is fixed by the route, so leaving this editable would let
          a payout be filed against a different account than the page it was created from. */}
      {!hideAccountSelection && !hasPayee && (
        <Field
          type="text"
          label="Account ID"
          name="projectExternalId"
          id="projectExternalId"
          component={FormikTextInput}
          isDisabled={isSubmitting}
          onChange={handleAccountIdChange}
          helpText={showOrganizationForm ? 'Enter an account id, or search for an organization below.' : undefined}
        />
      )}
      {showOrganizationForm && !hasAccountId && <PayeeOrganizationSelect onSelected={handlePayeeSelected} />}
      {hasPayee && <PayeeTransactionFields />}
      {values.projectExternalId?.length === ACCOUNT_ID_LENGTH && (
        <AddProjectTransactionForm
          payeeType={payeeType}
          // Forwarded, not hardcoded: inside the form this prop hides the account confirmation
          // panel — applicant name, address, email — which is what tells the user the id they
          // typed is the account they meant.
          hideAccountSelection={hideAccountSelection}
          allowHistoricalAdjustment={allowHistoricalAdjustment}
          showFincoEPCOption={showFincoEPCOption}
        />
      )}
    </Box>
  );
};

interface AddTransactionModalProps {
  isOpen: boolean;
  handleClose: () => void;
  /** Handles the project branch, so the caller's existing create behaviour is untouched. */
  handleSubmit: (values: CreateProjectTransactionPayload) => Promise<void>;
  modalId: string;
  modalTitle: string;
  submitButtonText: string;
  payeeType?: PayeeType;
  /**
   * Widened from IsoDateString deliberately. That type is a union of tens of thousands of literals,
   * and pulling it into this component's inference exceeds what the compiler can represent. Nothing
   * here reads the date — it is forwarded to the caller's create.
   */
  batchDate?: string;
  accountId?: string;
  /** Set on an account page, where the account is already decided and only its level applies. */
  hideAccountSelection?: boolean;
  allowHistoricalAdjustment?: boolean;
  showFincoEPCOption?: boolean;
}

export const AddTransactionModal = ({
  isOpen,
  handleClose,
  handleSubmit,
  modalId,
  modalTitle,
  submitButtonText,
  payeeType,
  batchDate,
  accountId,
  hideAccountSelection = false,
  allowHistoricalAdjustment = false,
  showFincoEPCOption = false,
}: AddTransactionModalProps) => {
  const [createPayeeTransaction] = useCreatePayeeTransactionMutation();
  const [submitError, setSubmitError] = useState<string>();

  // An account page has already decided the account, so there is no level to choose there.
  const showOrganizationForm = !hideAccountSelection;

  const initialValues = useMemo(
    () =>
      ({
        projectExternalId: accountId ?? '',
        payee: '',
        ...emptyProjectValues,
        ...emptyPayeeTransactionValues,
        ...projectOnlyFields,
      }) as unknown as AddTransactionFormValues,
    [accountId],
  );

  /**
   * Each branch validates on its own schema; sharing one would demand every field of both. Typed as
   * a loose Lazy on purpose — the project schema reaches IsoDateString, a union of tens of thousands
   * of literals, and letting the compiler union the two schemas exceeds what it can represent.
   */
  const validationSchema = useMemo<yup.Lazy<any>>(
    () => yup.lazy((values: any) => (hasSelectedPayee(values) ? payeeTransactionSchema : projectTransactionSchema)),
    [],
  );

  const closeAndReset = useCallback(
    (resetForm: () => void) => {
      setSubmitError(undefined);
      resetForm();
      handleClose();
    },
    [handleClose],
  );

  const onSubmit = useCallback(
    async (
      values: AddTransactionFormValues,
      { setSubmitting, resetForm }: FormikHelpers<AddTransactionFormValues>,
    ) => {
      setSubmitError(undefined);
      try {
        if (hasSelectedPayee(values)) {
          await createPayeeTransaction(toCreatePayeeTransactionPayload(values)).unwrap();
          toast.success('Transaction added successfully');
          resetForm();
          handleClose();
          return;
        }

        // The project branch keeps going through the caller, which owns the toast, the close and the
        // clawback description it prefixes. The payee-only fields are dropped rather than passed
        // along, so the project endpoint never receives keys it has no column for.
        const { payee: _payee, generalLedgerCategory: _glc, ...projectValues } = values;
        // Cast rather than reshaped. The project form sets fields the payload type does not declare
        // — eventDate and prePayment among them — and the project-only modal has always passed
        // them straight through, so narrowing to the declared keys here would silently drop them
        // from a path that is live in four views.
        await handleSubmit({
          ...projectValues,
          amount: Number(projectValues.amount),
          batchDate,
        } as CreateProjectTransactionPayload);
        resetForm();
      } catch (error: any) {
        setSubmitError(error?.data?.message ?? 'Could not add transaction');
      } finally {
        setSubmitting(false);
      }
    },
    [batchDate, createPayeeTransaction, handleClose, handleSubmit],
  );

  return (
    // The generic is pinned: left to infer, Formik unifies the values with the schema and trips the
    // same IsoDateString union blow-up the schema type above works around.
    <Formik<AddTransactionFormValues>
      validationSchema={validationSchema}
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize
      validateOnBlur
    >
      {({ isSubmitting, resetForm, submitForm }) => (
        <Form>
          <Modal
            isOpen={isOpen}
            maxWidth="4xl"
            ariaLabelledBy={modalId}
            onDismiss={() => {
              if (!isSubmitting) closeAndReset(resetForm);
            }}
          >
            <Modal.Header
              id={modalId}
              title={modalTitle}
              onDismiss={() => {
                if (!isSubmitting) closeAndReset(resetForm);
              }}
            />
            <Modal.Body background="secondary" childGap="lg">
              {submitError && <Alert variant="danger" message={submitError} hasIcon />}
              <AddTransactionFormFields
                showOrganizationForm={showOrganizationForm}
                hideAccountSelection={hideAccountSelection}
                payeeType={payeeType}
                allowHistoricalAdjustment={allowHistoricalAdjustment}
                showFincoEPCOption={showFincoEPCOption}
              />
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                tone="neutral"
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                // A button inside a form defaults to submit, so without this Cancel resets the form
                // and then submits it.
                type="button"
                onClick={() => closeAndReset(resetForm)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                type="submit"
                // The modal renders through a Reach dialog portal, so this button is not a DOM
                // descendant of Formik's <form> and clicking it fires no native submit. submitForm
                // is what actually submits — removing it does not de-duplicate anything, it stops
                // the form working. Every other payouts modal pairs the two for the same reason.
                onClick={submitForm}
              >
                {submitButtonText}
              </Button>
            </Modal.Footer>
          </Modal>
        </Form>
      )}
    </Formik>
  );
};
